import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);

    // Canonical URL
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.rel = 'canonical';
        document.head.appendChild(linkElement);
      }
      linkElement.href = canonical;
    }

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    if (canonical) updateMetaTag('og:url', canonical, true);
    if (ogImage) updateMetaTag('og:image', ogImage, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (ogImage) updateMetaTag('twitter:image', ogImage);

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]#page-schema');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('id', 'page-schema');
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]#page-schema');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, ogImage, schema]);
};

// Schema.org generators
export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${siteUrl}/#organization`,
    "name": "Speedy DMV Services",
    "image": `${siteUrl}/img/speedy-dmv-services.jpg`,
    "description": "Licensed Nevada DMV service provider offering fast vehicle registration, title transfers, license plates, and notary services in Las Vegas.",
    "url": siteUrl,
    "telephone": "+17029797202",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4525 S Sandhill Rd",
      "addressLocality": "Las Vegas",
      "addressRegion": "NV",
      "postalCode": "89121",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 36.0840,
      "longitude": -115.0677
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "15:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/speedydmvservices",
      "https://www.yelp.com/biz/speedy-dmv-services-las-vegas"
    ],
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas",
      "containedIn": {
        "@type": "State",
        "name": "Nevada"
      }
    }
  };
};

export const generateServiceSchema = (serviceName: string, description: string, _price?: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Speedy DMV Services",
      "telephone": "+17029797202",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "4525 S Sandhill Rd",
        "addressLocality": "Las Vegas",
        "addressRegion": "NV",
        "postalCode": "89121",
        "addressCountry": "US"
      }
    },
    "description": description,
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas"
    },
    "offers": {
      "@type": "Offer",
      "description": "Service fee from $120–$150 plus applicable Nevada DMV fees. Contact for exact quote.",
      "priceCurrency": "USD",
      "url": siteUrl
    }
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.url}`
    }))
  };
};

export const generateWebPageSchema = (pageName: string, description: string, pageUrl?: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageName,
    "description": description,
    "url": pageUrl || siteUrl,
    "publisher": {
      "@type": "Organization",
      "name": "Speedy DMV Services",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/img/logo.png`
      }
    }
  };
};

export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Speedy DMV Services Las Vegas",
    "url": siteUrl,
    "description": "Licensed Nevada DMV service provider in Las Vegas offering vehicle registration, title transfers, license plates, and notary services.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/faq?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};
